import * as React from 'react';
import { AnswerReviewFilterForm, Answer, AnswerStatsSummary, AnswerReviewFilterFormFields } from './types';
import AnswerReviewTable from './table';
import AnswerReviewForm from './filters';
import Summary from './summary';
import debounce from 'lodash/debounce';
import { paths } from 'config/path_helpers';
import fetch from 'utilities/fetch';
import cx from 'classnames';
import { Collapse } from 'react-bootstrap';
import { Waypoint } from 'react-waypoint';
import { PrimaryButton } from '@magoosh/lib/button';
import { bugsnagNotify } from 'utilities/bugsnag';
import trackEvent from 'utilities/track_event';

interface Props {
  filterInitialValues: AnswerReviewFilterForm;
  filterOptions: AnswerReviewFilterFormFields;
  answers: Answer[];
  answerStats: AnswerStatsSummary;
  currentPage: number;
  studentId: number;
  totalPages: number;
  path: string;
  showFilters: boolean;
  forHsDiagnostic: boolean;
}

interface State {
  answers: Answer[];
  answerStats: AnswerStatsSummary;
  isLoadingNewFilterResult: boolean;
  isLoadingMoreAnswers: boolean;
  showFilters: boolean;
  lastFilterSet: AnswerReviewFilterForm;
  page: number;
  totalPages: number;
}

export default class AnswerReviewPage extends React.Component<Props, State> {
  state: State = {
    answers: this.props.answers,
    answerStats: this.props.answerStats,
    isLoadingNewFilterResult: false,
    isLoadingMoreAnswers: false,
    showFilters: this.props.showFilters,
    lastFilterSet: this.props.filterInitialValues,
    page: this.props.currentPage,
    totalPages: this.props.totalPages
  };

  render() {
    return (
      <div>
        {this.props.forHsDiagnostic && 
          <PrimaryButton href={paths.dashboard()} className="pull-right">
            Return to Dashboard
          </PrimaryButton>
        }
        {!this.props.forHsDiagnostic && <div
          className={cx('collapse-trigger d-flex', this.state.showFilters ? '' : 'collapsed')}
          onClick={() => this.setState({ showFilters: !this.state.showFilters })}>
          <div className={cx(!this.state.showFilters && 'u-margin-T-xs', 'collapse-trigger-caret u-margin-R-xxs')}>
            <div></div>
          </div>
          <h3 className={cx(this.state.showFilters ? 'u-margin-T-n' : 'u-margin-T-xs', 'h3-small-hack')}>
            Filters
            <div className="collapse-trigger-subtitle">Date, exam section, subject and more</div>
          </h3>
        </div>}
        {!this.props.forHsDiagnostic && <Collapse in={this.state.showFilters} unmountOnExit={false}>
          <div>
            <AnswerReviewForm
              initialValues={this.props.filterInitialValues}
              options={this.props.filterOptions}
              onChange={this.loadAnswersWithNewFilters}
              showingStudentAnswers={this.showingStudentAnswers()}
            />
          </div>
        </Collapse>}

        <div className="page-header header-compact">
          <h3 className={this.props.forHsDiagnostic ? "" : "h3-small-hack"}>Summary</h3>
        </div>
        <Summary {...this.state.answerStats} showingStudentAnswers={this.showingStudentAnswers()} />

        <div className="page-header header-compact">
          <h3 className="h3-small-hack">Answers</h3>
        </div>
        <AnswerReviewTable
          answers={this.state.answers}
          isLoading={this.state.isLoadingNewFilterResult}
          showingStudentAnswers={this.props.studentId != null}
          forHsDiagnostic={this.props.forHsDiagnostic}
        />
        {this.state.page < this.state.totalPages && (
          <div className="text-center u-margin-V-m">
            <Waypoint onEnter={this.loadMoreAnswers} bottomOffset="-300px" />
            <PrimaryButton onClick={this.loadMoreAnswers} submitting={this.state.isLoadingMoreAnswers}>
              {this.state.isLoadingMoreAnswers ? 'Loading More Answers...' : 'Show More Answers'}
            </PrimaryButton>
          </div>
        )}
        <div className="u-margin-B-m" />
      </div>
    );
  }

  loadAnswersWithNewFilters = debounce(async values => {
    this.setState({
      isLoadingNewFilterResult: true
    });

    try {
      const result: Props = await fetch(paths.api.answerReview(this.props.studentId), {
        method: 'POST',
        body: JSON.stringify(values)
      });
      window.history.replaceState('', '', result.path);

      this.setState({
        answers: result.answers,
        answerStats: result.answerStats,
        isLoadingNewFilterResult: false,
        lastFilterSet: values,
        page: 1,
        totalPages: result.totalPages
      });

      trackEvent('review_page_filter', values.review);
    }
    catch(e) {
      // Leave the state as loading new filter result.
      // The user can still edit the filters form and re-submit
      bugsnagNotify(e);
    }
  }, 600);

  loadMoreAnswers = async () => {
    if (this.state.page >= this.state.totalPages) {
      return;
    }

    if (this.state.isLoadingMoreAnswers || this.state.isLoadingNewFilterResult) {
      return;
    }

    this.setState({
      isLoadingMoreAnswers: true
    });

    try {
      const result: Props = await fetch(paths.api.answerReview(this.props.studentId), {
        method: 'POST',
        body: JSON.stringify({
          ...this.state.lastFilterSet,
          page: this.state.page + 1
        })
      });

      this.setState({
        answers: [...this.state.answers, ...result.answers],
        isLoadingMoreAnswers: false,
        page: result.currentPage
      });
    } catch(e) {
      // Leave the state as loading
      bugsnagNotify(e);
    }
  };

  showingStudentAnswers = () => {
    return this.props.studentId != null;
  }
}
